import PropTypes from "prop-types"
import React, { useEffect, useState, useMemo } from "react"
import {
  Col,
  Row,
  Container,
  Button,
  Alert,
  Spinner,
} from "reactstrap"

import Breadcrumbs from "../../components/Common/Breadcrumb2"
import { withTranslation } from "react-i18next"
import "../../components/Common/ts-css.css"

import { get } from "helpers/api_helper"
import { getApiErrorMessage } from "helpers/api_error_helper"
import { getSurveyEntryChannel } from "helpers/line_campaign_helper"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"

import Step2 from "./components/Step2"

const SurveyLineCampaignView = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [formValues, setFormValues] = useState({})
  const [categoryOptions, setCategoryOptions] = useState([])
  const [allSurveyOptions, setAllSurveyOptions] = useState([])
  const [lineOaOptions, setLineOaOptions] = useState([])
  const [loadError, setLoadError] = useState("")

  const selectedSurveyName = useMemo(() => {
    if (!formValues.surveyId) return ""
    const survey = allSurveyOptions.find(s => String(s.id) === String(formValues.surveyId))
    return survey ? survey.name : ""
  }, [formValues.surveyId, allSurveyOptions])

  const validation = useMemo(() => ({ values: formValues }), [formValues])

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [catResult, surveyResult, lineOaResult, campaignResult] = await Promise.all([
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/category"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/dropdown/questionnaire"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-oa/dropdown"),
          get(process.env.REACT_APP_BACKEND_URL + "/v1/admin/line-campaign/" + id),
        ])

        if (catResult?.data) setCategoryOptions(catResult.data)
        if (surveyResult?.data) setAllSurveyOptions(surveyResult.data)
        if (lineOaResult?.data) setLineOaOptions(lineOaResult.data)

        if (campaignResult?.data) {
          const d = campaignResult.data
          const lineOaList = lineOaResult?.data || []

          let surveyUrl = d.survey_url || ""
          let surveyMobileUrl = ""
          if (d.survey_id && d.line_oa_id) {
            const survey = (surveyResult?.data || []).find(s => String(s.id) === String(d.survey_id))
            const oa = lineOaList.find(o => String(o.id) === String(d.line_oa_id))
            if (survey && survey.uuid_link) {
              const base = `${process.env.REACT_APP_FRONTWEB_URL}/pdpa/chat/${survey.uuid_link}`
              const lineParams = ["identity=line"]
              if (oa && oa.channel_id_login) {
                lineParams.push(`cid=${oa.channel_id_login}`)
              }
              if (d.id) {
                lineParams.push(`lcid=${d.id}`)
              }
              const entryChannel = getSurveyEntryChannel(d.survey_url, d.id, survey.uuid_link)
              if (entryChannel) lineParams.push(`entry_channel=${entryChannel}`)
              surveyUrl = lineParams.length > 0 ? `${base}?${lineParams.join("&")}` : base
              surveyMobileUrl = surveyUrl
            }
          }

          setFormValues({
            id: d.id,
            lineOaId: d.line_oa_id || "",
            categoryId: d.category_id || "",
            surveyId: d.survey_id || "",
            surveyUrl,
            surveyMobileUrl,
            recipients: d.recipients || "all",
            recipientsLabel: d.recipients_label || "",
            audienceGender: Array.isArray(d.audience_gender) ? d.audience_gender : [],
            audienceAge: Array.isArray(d.audience_age) ? d.audience_age : [],
            audienceOs: Array.isArray(d.audience_os) ? d.audience_os : [],
            audienceFriendshipPeriod: Array.isArray(d.audience_friendship_period) ? d.audience_friendship_period : [],
            audienceCity: Array.isArray(d.audience_city) ? d.audience_city : [],
            broadcastTimeType: d.broadcast_time_type || "now",
            scheduleDate: d.schedule_date ? moment.utc(d.schedule_date).format("YYYY-MM-DD") : "",
            scheduleTime: d.schedule_time || "",
            contentBlocks: Array.isArray(d.content_blocks) && d.content_blocks.length > 0
              ? d.content_blocks
              : [],
            startDate: d.start_date ? moment.utc(d.start_date).format("YYYY-MM-DD") : "",
            endDate: d.end_date ? moment.utc(d.end_date).format("YYYY-MM-DD") : "",
            campaignName: d.campaign_name || "",
            specificUserLineUids: Array.isArray(d.specific_user_line_uids) ? d.specific_user_line_uids : [],
            specificUserFileName: d.specific_user_file_name || "",
            status: d.status || "",
          })
        }
      } catch (error) {
        setLoadError(getApiErrorMessage(error, "Unable to load campaign details."))
      }
      setIsLoading(false)
    }

    fetchAll()
  }, [id])

  document.title = "View Campaign | " + process.env.REACT_APP_CMS_TITLE

  if (isLoading) {
    return <Spinner color="primary" />
  }

  return (
    <div className="page-content">
      <Container fluid={true}>
        <Breadcrumbs
          title={"Survey Line Campaign"}
          breadcrumbItems={[
            { title: "Survey Line Campaign", link: "survey-line-campaign" },
            { title: "View Campaign", link: `view/${id}` },
          ]}
        />

        <h4 className="mb-4" style={{ fontWeight: "600" }}>
          View Campaign
        </h4>

        {loadError && <Alert color="danger">{loadError}</Alert>}
        {!loadError && <Step2
          validation={validation}
          pageView="view"
          surveyName={selectedSurveyName}
          categoryOptions={categoryOptions}
          lineOaOptions={lineOaOptions}
        />}

        <Row className="mt-4">
          <Col>
            <Button
              type="button"
              outline
              color="secondary"
              style={{
                minWidth: "120px",
                backgroundColor: "#fff",
                borderColor: "#ced4da",
                color: "#495057",
              }}
              onClick={() => navigate("/survey-line-campaign")}
            >
              <i className="bx bx-arrow-back me-1"></i>
              Back
            </Button>
          </Col>
        </Row>
      </Container>
    </div>
  )
}

SurveyLineCampaignView.propTypes = {
  t: PropTypes.any,
}

export default withTranslation()(SurveyLineCampaignView)
